import { useState } from "react";
import { AVATAR_KEYS, Avatar } from "../components/Avatar";
import { PresetPicker, usePresets } from "../components/Capabilities";
import { api, ApiError } from "../lib/api";
import { Field } from "./Setup";

const COLORS = [
  { name: "red", value: "#e2445c" },
  { name: "green", value: "#2b9e5f" },
  { name: "orange", value: "#f2a03d" },
  { name: "blue", value: "#3d7ea6" },
  { name: "purple", value: "#8659a8" },
  { name: "teal", value: "#1f9e9e" },
];

/* Add a kid.
 *
 * A sign-in is OPTIONAL and off by default. A four-year-old may not need one
 * for months -- the parent can track their jars and chores without it -- which
 * is exactly why the profile is a separate record from the login.
 */
export function AddChild({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const catalogue = usePresets();
  const [name, setName] = useState("");
  const [preset, setPreset] = useState("age_4");
  const [avatar, setAvatar] = useState(AVATAR_KEYS[0]);
  const [color, setColor] = useState(COLORS[0].value);
  const [wantsLogin, setWantsLogin] = useState(false);
  const [loginName, setLoginName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/children", {
        displayName: name.trim(),
        capabilityPreset: preset,
        avatar,
        color,
        ...(wantsLogin ? { loginName: loginName.trim().toLowerCase(), pin } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add them.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
      <section className="card" style={{ display: "grid", gap: 14 }}>
        <h2 style={{ fontSize: 20 }}>Add a kid</h2>

        <Field label="Their name">
          <input value={name} onChange={(e) => setName(e.target.value)}
                 required maxLength={40} placeholder="Emma" autoFocus />
        </Field>

        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 600, fontSize: 14, padding: 0, marginBottom: 8 }}>
            Pick a face
          </legend>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))",
              gap: 8,
            }}
          >
            {AVATAR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setAvatar(key)}
                aria-pressed={avatar === key}
                aria-label={key}
                style={{
                  padding: 6, minHeight: 0, boxShadow: "none",
                  borderColor: avatar === key ? "var(--ink)" : "var(--paper-edge)",
                  background: avatar === key ? "var(--paper-edge)" : "var(--paper)",
                }}
              >
                <Avatar name={key} size={40} ring={avatar === key ? color : null} />
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 600, fontSize: 14, padding: 0, marginBottom: 8 }}>
            Pick a colour
          </legend>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                aria-pressed={color === c.value}
                aria-label={c.name}
                style={{
                  width: 48, height: 48, minHeight: 0, padding: 0,
                  borderRadius: "50%", background: c.value,
                  borderColor: color === c.value ? "var(--ink)" : "transparent",
                  borderWidth: color === c.value ? 4 : 2,
                  boxShadow: color === c.value ? "0 3px 0 var(--ink)" : "none",
                }}
              />
            ))}
          </div>
        </fieldset>
      </section>

      <section className="card" style={{ display: "grid", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 18 }}>What can they do to start?</h2>
          <p style={{ margin: "2px 0 0", fontSize: 13.5, color: "var(--ink-2)" }}>
            Pick the group that fits them. Every switch stays yours to change
            afterwards, one at a time.
          </p>
        </div>
        {catalogue ? (
          <PresetPicker presets={catalogue.presets} value={preset}
                        onChange={setPreset} />
        ) : (
          <p style={{ margin: 0, color: "var(--ink-3)" }}>Loading…</p>
        )}
      </section>

      <section className="card" style={{ display: "grid", gap: 12 }}>
        <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={wantsLogin}
            onChange={(e) => setWantsLogin(e.target.checked)}
            style={{ width: 22, height: 22, minHeight: 0, flex: "0 0 auto", marginTop: 2 }}
          />
          <span>
            <span style={{ display: "block", fontWeight: 600 }}>
              Give them their own sign-in
            </span>
            <span style={{ fontSize: 13, color: "var(--ink-2)" }}>
              A picture and a 4-digit number. Skip it for now if they&rsquo;re too
              young &mdash; you can add it any time.
            </span>
          </span>
        </label>

        {wantsLogin && (
          <>
            <Field label="Sign-in name" hint="Lowercase, no spaces.">
              <input value={loginName} onChange={(e) => setLoginName(e.target.value)}
                     required={wantsLogin} autoCapitalize="none" maxLength={40}
                     placeholder="emma" />
            </Field>
            <Field label="Secret number" hint="Any four digits they can remember on their own.">
              <input
                inputMode="numeric" pattern="\d{4}" maxLength={4}
                value={pin} required={wantsLogin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="money"
                style={{ fontSize: 24, letterSpacing: "0.4em", textAlign: "center" }}
              />
            </Field>
          </>
        )}
      </section>

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, margin: 0 }}>
          {error}
        </p>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={busy} style={{ flex: 1 }}>
          {busy ? "Adding…" : "Add kid"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
